import { queryOptions, type QueryClient, type QueryKey } from "@tanstack/react-query";
import type { TimelineService, TTimelineFilterParams } from "@/services/timeline.service";
import type { TTimelineResponse } from "@/types/timeline-view";
import { FRESHNESS } from "./policies";
import { scopedKey, scopedRead, type QueryScope } from "./scope";

/**
 * The timeline windows (NG-2812): the workspace Timeline, a project's Timeline
 * tab, an event's Timeline tab, and the time off and holidays a project's Gantt
 * draws behind its bars.
 *
 * `[slug, uid, 'timeline', {scope, id, start, end, filters}]`, one entry per
 * window asked for. `scope` names the endpoint: the workspace, one project
 * (`id` = the project), or one work item's subtree (`id` = the work item).
 * The key carries the whole request, so the Gantt's time-off read and an open
 * Timeline no longer share one slot, as they did in the singleton store.
 *
 * Freshness: `mutable`, without the 60 s visible poll, which is what the store
 * did. A write anywhere in the app that can move a bar re-reads it
 * (`schedule-invalidation.ts`), and so do focus and a revisit once stale.
 *
 * @writers `setTimelineItemSchedule` (the drag's optimistic move and its
 * rollback), `installScheduleWriteInvalidation` (any write whose URL can move a
 * bar), and the view's Retry and after-save re-read.
 */

type TimelineScope = "workspace" | "project" | "work-item";

export interface TimelineWindowParams {
  scope: TimelineScope;
  /** The project or work item the window is about; `null` for the workspace. */
  id: string | null;
  start: string;
  end: string;
  filters?: TTimelineFilterParams;
}

type TimelineReader = Pick<
  TimelineService,
  "getTimeline" | "getProjectTimeline" | "getWorkItemTimeline"
>;

const POLICY = { ...FRESHNESS.mutable, refetchInterval: false } as const;

/** Every timeline window in `scope`, the prefix a write invalidates. */
export function timelinePrefix(scope: QueryScope): QueryKey {
  return scopedKey(scope, "timeline");
}

export function timelineKey(scope: QueryScope, params: TimelineWindowParams) {
  return scopedKey(scope, "timeline", {
    scope: params.scope,
    id: params.id,
    start: params.start,
    end: params.end,
    filters: params.filters ?? null,
  });
}

/** One timeline window. */
export function timelineQuery(
  service: TimelineReader,
  scope: QueryScope,
  params: TimelineWindowParams,
) {
  return queryOptions({
    ...POLICY,
    queryKey: timelineKey(scope, params),
    queryFn: scopedRead<TTimelineResponse>(
      (request) => {
        const { start, end, filters, id } = params;
        if (params.scope === "work-item" && id)
          return service.getWorkItemTimeline(scope.tenant, id, start, end, filters, request);
        if (params.scope === "project" && id)
          return service.getProjectTimeline(scope.tenant, id, start, end, filters, request);
        return service.getTimeline(scope.tenant, start, end, filters, request);
      },
      { retry: "safe-read" },
    ),
  });
}

/** Where an item sits: its dates, and the datetimes a timed event also carries. */
export interface TimelineItemSchedule {
  start: string;
  end: string;
  startDatetime?: string;
  endDatetime?: string;
}

/**
 * Move one item in the window under `key`, so the bars redraw where it now
 * sits: the drag calls it on every pointer move, and again with the old
 * schedule to put a refused drop back.
 *
 * Dates and datetimes move together in ONE write because the layouts read
 * different halves of the same move: the Gantt and the month grid place a bar
 * by `start`/`end`, the hourly grid places a timed event by its datetimes. Two
 * steps would leave a frame where the bar sits on a day its own dates disagree
 * with (NG-2041). Omit the datetimes for an item that has none to move.
 *
 * Only the moved item and the items array are new objects, so the cost per
 * pointer move is one shallow copy of the window; the row and lane memos it
 * re-runs ran on every move in the store too (its `revision` counter). A
 * window that is not cached yet, or an item not in it, is left alone.
 */
export function setTimelineItemSchedule(
  client: QueryClient,
  key: QueryKey,
  itemId: string,
  schedule: TimelineItemSchedule,
): void {
  client.setQueryData<TTimelineResponse>(key, (current) => {
    const item = current?.items.find((it) => it.id === itemId);
    if (!current || !item) return current;
    const { start, end, startDatetime, endDatetime } = schedule;
    const datesMoved = item.start !== start || item.end !== end;
    const timesMoved =
      startDatetime !== undefined &&
      endDatetime !== undefined &&
      (item.start_datetime !== startDatetime || item.end_datetime !== endDatetime);
    if (!datesMoved && !timesMoved) return current;
    const moved = timesMoved
      ? { ...item, start, end, start_datetime: startDatetime, end_datetime: endDatetime }
      : { ...item, start, end };
    return { ...current, items: current.items.map((it) => (it.id === itemId ? moved : it)) };
  });
}
